import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useAccountMappings, useSettings } from '@/features/accounting/api'
import { type PaymentTerm, usePaymentTerms } from '@/features/accounting-tax/api'
import { useCustomers } from '@/features/customers/hooks/use-customers'
import { useCompanySettings } from '@/features/settings/api'
import { api } from '@/lib/api'
import { useCompanyTaxRates } from '@/lib/tax-rates'
import { invoicesKey, unwrap } from './invoice-api-helpers'
import type {
  Account,
  EInvoiceSubmission,
  Invoice,
  InvoiceFilters,
  InvoiceSummary,
  InvoiceUnit,
} from './invoice-api-types'
import { DEFAULT_PRESETS } from './invoice-form-helpers'

export function useInvoiceUnits(companyId: string | null) {
  return useQuery<InvoiceUnit[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'invoice-units'] as const,
    enabled: Boolean(companyId),
    queryFn: async () => {
      if (!companyId) return []
      const response = await api.GET('/directory/units-of-measure', {
        headers: { 'X-Company-Id': companyId },
        params: { query: { include_inactive: true } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load units')
      return response.data?.data ?? []
    },
  })
}

export function useInvoiceSummary(
  companyId: string | null,
  filters: InvoiceFilters = {},
  options?: Omit<UseQueryOptions<InvoiceSummary, Error>, 'queryKey' | 'queryFn'>,
) {
  const summaryFilters = {
    postingStatus: filters.postingStatus,
    paymentStatus: filters.paymentStatus,
    status: filters.status,
    customer: filters.customer,
    analysisTagIds: filters.analysisTagIds,
    from: filters.from,
    to: filters.to,
    overdue: filters.overdue,
  }

  return useQuery<InvoiceSummary, Error>({
    queryKey: [...invoicesKey(companyId), 'summary', summaryFilters],
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<InvoiceSummary> => {
      if (!companyId) throw new Error('Cannot summarize invoices without an active company.')
      const response = await api.GET('/accounting/invoices/summary', {
        headers: { 'X-Company-Id': companyId },
        params: { query: invoiceIndexQuery(filters, false) },
      })
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to load invoice summary')
      return response.data
    },
  })
}

function invoiceIndexQuery(filters: InvoiceFilters, includePaging: boolean) {
  return {
    'posting_status[]': filters.postingStatus ? [filters.postingStatus] : undefined,
    'payment_status[]': filters.paymentStatus ? [filters.paymentStatus] : undefined,
    status: filters.status || undefined,
    customer: filters.customer,
    'analysis_tag_ids[]': filters.analysisTagIds?.length ? filters.analysisTagIds : undefined,
    from: filters.from,
    to: filters.to,
    overdue: filters.overdue,
    ...(includePaging
      ? {
          page: filters.page,
          per_page: apiPageSize(filters.perPage),
          sort: filters.sort,
          direction: filters.direction,
        }
      : {}),
  }
}

function apiPageSize(value?: number): '10' | '25' | '50' | '100' | undefined {
  switch (value) {
    case 10:
      return '10'
    case 25:
      return '25'
    case 50:
      return '50'
    case 100:
      return '100'
    default:
      return undefined
  }
}

export function useInvoice(
  companyId: string | null,
  invoiceId: string | null,
  options?: Omit<UseQueryOptions<Invoice | null, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({ ...invoiceQuery(companyId, invoiceId), ...options })
}

export function invoiceQuery(companyId: string | null, invoiceId: string | null) {
  return {
    queryKey: [...invoicesKey(companyId), invoiceId] as const,
    enabled: Boolean(companyId && invoiceId),
    queryFn: async (): Promise<Invoice | null> => {
      if (!companyId || !invoiceId) return null
      const response = await api.GET('/accounting/invoices/{invoice}', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { invoice: invoiceId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load invoice')
      return unwrap<Invoice>(response.data ?? {}, 'invoice')
    },
  } satisfies UseQueryOptions<Invoice | null, Error>
}

export function useEInvoiceSubmissions(
  companyId: string | null,
  invoiceId: string | null,
  options?: Omit<UseQueryOptions<EInvoiceSubmission[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<EInvoiceSubmission[], Error>({
    queryKey: [...invoicesKey(companyId), invoiceId, 'einvoice-submissions'] as const,
    enabled: Boolean(companyId && invoiceId),
    ...options,
    queryFn: async (): Promise<EInvoiceSubmission[]> => {
      if (!companyId || !invoiceId) return []
      const response = await api.GET('/accounting/invoices/{invoice}/einvoice-submissions', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { invoice: invoiceId } },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to load e-invoice submissions')
      }
      return unwrap<EInvoiceSubmission[]>(response.data ?? {}, 'einvoice-submissions') ?? []
    },
  })
}

export function useAccounts(
  companyId: string | null,
  options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Account[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'accounts'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<Account[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/accounts', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load accounts')
      return unwrap<Account[]>(response.data ?? {}, 'accounts') ?? []
    },
  })
}

/** The shared company tax-rate query; this screen's company gates it. */
export function useTaxRates(companyId: string | null) {
  return useCompanyTaxRates({ enabled: Boolean(companyId) })
}

export function useInvoiceFormReferenceData(companyId: string | null) {
  const accounts = useAccounts(companyId)
  const accountMappings = useAccountMappings(companyId)
  const taxRates = useTaxRates(companyId)
  const companySettings = useCompanySettings(companyId)
  const accountingSettings = useSettings(companyId)
  const paymentTerms = usePaymentTerms(companyId)
  const customersQuery = useCustomers()
  const availableTerms: PaymentTerm[] =
    paymentTerms.data && paymentTerms.data.length > 0
      ? paymentTerms.data.filter((term) => term.is_active)
      : DEFAULT_PRESETS
  const defaultPaymentTermId = accountingSettings.data?.default_payment_terms_id ?? null
  const defaultPaymentTerm = availableTerms.find((term) => term.id === defaultPaymentTermId)

  return {
    accounts,
    accountMappings,
    taxRates,
    customersQuery,
    availableTerms,
    defaultPaymentTermId,
    defaultPaymentTerm,
    defaultTermsDays:
      defaultPaymentTerm?.net_days ?? companySettings.data?.default_payment_terms_days ?? 30,
  }
}
